import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { tickerDetailService } from '../../services/tickerDetailService';

interface Props { symbol: string; }

interface MetaItem {
  label: string;
  value: string | number | null | undefined;
  isLink?: boolean;
}

export function CompanyProfile({ symbol }: Props): React.ReactElement {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tickerCompany', symbol],
    queryFn: () => tickerDetailService.getCompany(symbol),
    staleTime: 24 * 60 * 60 * 1000,
    enabled: !!symbol,
  });

  const profile = data?.data;

  if (isLoading) return <SectionSkeleton />;
  if (isError || !profile) return <SectionError message="Failed to load company profile" />;

  const meta: MetaItem[] = [
    { label: 'Sector', value: profile.sector },
    { label: 'Industry', value: profile.industry },
    { label: 'CEO', value: profile.ceo },
    { label: 'Employees', value: profile.fullTimeEmployees },
    { label: 'Exchange', value: profile.exchange },
    { label: 'IPO Date', value: profile.ipoDate },
    { label: 'Country', value: profile.country },
    { label: 'Website', value: profile.website, isLink: true },
  ];

  return (
    <section className="p-4 border-b border-dark-border">
      <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">
        Company Profile
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <p className="text-gray-400 text-sm leading-relaxed">{profile.description || '\u2014'}</p>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {meta.map(({ label, value, isLink }) => (
            <React.Fragment key={label}>
              <dt className="text-gray-400">{label}</dt>
              <dd className="text-gray-200 truncate">
                {isLink && value ? (
                  <a href={String(value)} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">
                    {String(value).replace(/^https?:\/\//, '')}
                  </a>
                ) : value != null ? String(value) : '\u2014'}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      </div>
    </section>
  );
}

// Shared skeleton + error helpers (exported for use by all other sections)
export function SectionSkeleton(): React.ReactElement {
  return (
    <div className="p-4 border-b border-dark-border">
      <div className="animate-pulse space-y-2">
        <div className="h-4 w-32 bg-dark-border rounded" />
        <div className="h-3 w-full bg-dark-border rounded" />
        <div className="h-3 w-3/4 bg-dark-border rounded" />
      </div>
    </div>
  );
}

export function SectionError({ message }: { message: string }): React.ReactElement {
  return (
    <div className="p-4 border-b border-dark-border text-bear-red text-sm">
      {message}
    </div>
  );
}
